/* Copyright (c) 2023-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback } from "react";
import {
  GenerateDatesFromCronInput,
  useGenerateDatesFromCronMutation,
} from "~/generated/graphql";
import { showError } from "~/services/toast";

type Props = {
  onSuccess: (dates: string[]) => void;
  planId?: string;
};

export function useGenerateDatesFromCron({ onSuccess, planId }: Props) {
  const [generateDatesFromCronMutation, { data, loading }] =
    useGenerateDatesFromCronMutation({
      onCompleted: (response) =>
        onSuccess(response.generateDatesFromCron.dates ?? []),
      onError: (error) => showError(error.message),
      update(cache, { data: updateData }, { variables }) {
        if (updateData?.generateDatesFromCron && variables?.input) {
          cache.modify({
            id: cache.identify({
              __typename: "Plan",
              id: planId,
            }),
            fields: {
              nextExecutionDates: () =>
                updateData.generateDatesFromCron.dates ?? [],
            },
          });

          cache.gc();
        }
      },
    });

  const generateDatesFromCron = useCallback(
    (inputData: CronData) =>
      generateDatesFromCronMutation({
        variables: {
          input: mapCronData(inputData),
        },
      }),
    [generateDatesFromCronMutation]
  );

  return { generateDatesFromCron, data, loading } as const;
}

export type CronData = {
  [CronField.CRONE_EXPRESSION]: string;
  [CronField.LOCATION]: string;
  [CronField.OFFSET_IN_MINUTES]?: number;
  [CronField.COUNT_LIMIT]?: number;
};

enum CronField {
  CRONE_EXPRESSION = "cron_expression",
  LOCATION = "location",
  OFFSET_IN_MINUTES = "offset_in_minutes",
  COUNT_LIMIT = "count_limit",
}

function mapCronData(data: CronData): GenerateDatesFromCronInput {
  return {
    cronExpression: data.cron_expression,
    location: data.location,
    countLimit: data.count_limit,
    offsetInMinutes: data.offset_in_minutes,
  };
}
